import { useState } from 'react';

import { Button } from '../components/Button.js';
import { Icon, WavingHandIcon } from '../components/Icon.js';
import type { ClosingVariant } from '../fixtures/domain.js';
import { useDismiss } from '../util/useDismiss.js';
import styles from './EndChatDialog.module.css';

interface EndChatDialogProps {
  variant: ClosingVariant;
  counterpartName: string;
  sending?: boolean;
  onCancel: () => void;
  onConfirm: (text: string) => void;
}

const COPY: Record<ClosingVariant, { subtitle: string; confirm: string; sendingLabel: string }> = {
  reject: {
    subtitle: 'This ends the chat and rejects the applicant.',
    confirm: 'End & reject',
    sendingLabel: 'Rejecting…',
  },
  close: {
    subtitle: 'This ends the chat.',
    confirm: 'End chat',
    sendingLabel: 'Ending…',
  },
};

/**
 * Warm template copy seeded into the editable field. The mock drafts this with
 * AI (`useClosingDraft`, with this as the fallback); the UI-only rewrite seeds
 * the same template statically — the AI path returns with the wiring epic.
 */
function closingTemplate(variant: ClosingVariant, candidateName: string): string {
  const name = candidateName.split(' ')[0] || candidateName;
  return variant === 'reject'
    ? `Hi ${name}, thank you so much for your time and for sharing your experience with us. ` +
        `After careful consideration, we've decided not to move forward with your application for ` +
        `this role. We truly appreciate your interest and wish you all the best in your search.`
    : `Hi ${name}, thanks so much for chatting with us — it was great connecting. We'll keep your ` +
        `details on hand and reach out if a suitable role comes up. Wishing you all the best!`;
}

/**
 * Modal for ending a conversation with a closing message. `reject` also marks a
 * job applicant rejected; `close` just wraps up a direct chat. The employer
 * edits the seeded draft, then sends and closes.
 */
export function EndChatDialog({
  variant,
  counterpartName,
  sending = false,
  onCancel,
  onConfirm,
}: EndChatDialogProps) {
  const copy = COPY[variant];
  const [text, setText] = useState(() => closingTemplate(variant, counterpartName));
  const [edited, setEdited] = useState(false);

  useDismiss(true, () => {
    if (!sending) onCancel();
  });

  const canSend = text.trim() !== '' && !sending;

  return (
    <div className={styles.overlay} onMouseDown={() => !sending && onCancel()}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <span className={styles.iconBadge}>
            <WavingHandIcon size={20} />
          </span>
          <div className={styles.heading}>
            <h2 className={styles.title}>Send a closing message</h2>
            <p className={styles.subtitle}>{copy.subtitle}</p>
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.fieldHead}>
            {edited ? (
              <span className={styles.fieldLabel}>Message</span>
            ) : (
              <span className={styles.source}>
                <Icon name="bolt" size={13} />
                Template. Edit before sending
              </span>
            )}
          </div>

          <textarea
            className={styles.textarea}
            value={text}
            disabled={sending}
            rows={5}
            onChange={(e) => {
              setText(e.target.value);
              setEdited(true);
            }}
            placeholder="Write a short, respectful note…"
          />
        </div>

        <div className={styles.footer}>
          <Button variant="ghost" onClick={onCancel} disabled={sending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => onConfirm(text.trim())} disabled={!canSend}>
            {sending ? copy.sendingLabel : copy.confirm}
          </Button>
        </div>
      </div>
    </div>
  );
}
